import React, { useEffect, useState, useCallback } from 'react';
import { SmartSegmentCard } from './SegmentCard';
import _chunk from 'lodash/chunk';
import { SlColumn, SlRow, StacklineColor } from '@stackline/ui';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import { Segment } from 'sl-api-connector';
import Box from '@mui/material/Box';
import { shouldHidePagination } from 'src/components/BeaconV2Components/EntityTableRefresh/utils';
import { SegmentDeletionModal } from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentDeletionModal';

const CARDS_PER_ROW = 4;
const CARDS_PER_PAGE = 20;

interface SegmentCardGridProps {
  segments: Segment[];
  indexName?: string;
  fieldName?: string;
  metricDisplayName?: string;
  color?: StacklineColor;
  isTopicSegment?: boolean;
  pendingSegments?: string[];
}

/**
 * Shows a grid of segment cards
 */
const SegmentCardGrid = React.memo(
  ({
    segments = [],
    indexName,
    fieldName,
    metricDisplayName,
    color,
    isTopicSegment,
    pendingSegments = []
  }: SegmentCardGridProps) => {
    const [deleteSegmentModalOpen, setDeleteSegmentModalOpen] = useState(false);
    const [segmentToDelete, setSegmentToDelete] = useState(null);

    const totalPages = Math.ceil(segments.length / CARDS_PER_PAGE);
    const [page, setPage] = useState(1);

    const startIndex = (page - 1) * CARDS_PER_PAGE;
    const endIndex = startIndex + CARDS_PER_PAGE;

    const handleNextPage = useCallback(() => {
      setPage((prev) => Math.min(prev + 1, totalPages));
    }, [totalPages]);

    const handlePrevPage = useCallback(() => {
      setPage((prev) => Math.max(prev - 1, 1));
    }, []);

    /**
     * Reset the page when the search changes
     */
    useEffect(() => {
      setPage(1);
    }, [segments.length]);

    return (
      <SlColumn spacing="mdl" widths="full">
        {_chunk(segments.slice(startIndex, endIndex), CARDS_PER_ROW).map((row, index) => {
          return (
            <SlRow key={index} spacing="mdl">
              {row.map((segment) => {
                const isPending = pendingSegments.includes(segment.id);

                return (
                  <SmartSegmentCard
                    indexName={indexName}
                    fieldName={fieldName}
                    key={segment.id}
                    segment={segment}
                    metricDisplayName={metricDisplayName}
                    color={color}
                    isTopicSegment={isTopicSegment}
                    isPending={isPending}
                    handleDelete={() => {
                      setSegmentToDelete(segment.id);
                      setDeleteSegmentModalOpen(true);
                    }}
                  />
                );
              })}
            </SlRow>
          );
        })}
        <Box width="100%" display="flex" justifyContent="flex-end">
          {shouldHidePagination({ page, pageSize: CARDS_PER_PAGE, rowCount: (segments || []).length }) ? null : (
            <Pagination
              currentPage={page}
              onClickNext={() => handleNextPage()}
              onClickPrevious={() => handlePrevPage()}
              totalPages={totalPages}
            />
          )}
        </Box>
        {deleteSegmentModalOpen && (
          <div style={{ position: 'absolute' }}>
            <SegmentDeletionModal
              isTopicSegment={isTopicSegment}
              segmentToDelete={segmentToDelete}
              open={deleteSegmentModalOpen}
              handleClose={() => {
                setDeleteSegmentModalOpen(false);
                setSegmentToDelete(null);
              }}
            />
          </div>
        )}
      </SlColumn>
    );
  }
);

SegmentCardGrid.displayName = 'SegmentCardGrid';

export default SegmentCardGrid;
